import { SortingState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, ProgressBar, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { createSelector } from "reselect";

import { Axios } from "api/axios";
import EditableCell from "Common/EditableCell";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { ItemActionMenu } from "Components/MenuActionButton/ItemActionMenu";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { formatDate } from "helpers/functions/formatDate";
import { toast } from "react-toastify";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  deleteItemById,
  fetchItems as OnfetchItems,
  updateStatusInItem as onUpdateStatusInItem,
} from "slices/item/thunk";
import Swal from "sweetalert2";
import PriceStatusChip from "./PriceStatusChip";
import {
  fetchActiveWebSyncJob,
  fetchWebSyncJob,
  isWebSyncJobTerminal,
  persistWebSyncJobId,
  readStoredWebSyncJobId,
  startWebSyncJob,
  type WebSyncJobSummary,
} from "./webSyncJob";

/** Do not resize .swal2-icon — SWAL2 checkmark/warning animations use fixed pseudo-element layout. */
function applyItemSwalPopupStyles(popup: HTMLElement | null | undefined) {
  if (!popup) return;
  popup.style.textAlign = "center";
  const htmlContainer = popup.querySelector(
    ".swal2-html-container"
  ) as HTMLElement | null;
  if (htmlContainer) {
    htmlContainer.style.textAlign = "left";
  }
  const title = popup.querySelector(".swal2-title") as HTMLElement | null;
  if (title) {
    title.style.fontSize = "1.1rem";
    title.style.fontWeight = "600";
  }
  const confirmButton = popup.querySelector(
    ".swal2-confirm"
  ) as HTMLElement | null;
  if (confirmButton) {
    confirmButton.style.backgroundColor = "#0073BB";
    confirmButton.style.borderColor = "#0073BB";
    confirmButton.style.color = "#ffffff";
  }
}

const Item = () => {
  document.title = "Item List";
  const dispatch = useDispatch<any>();

  const selectItem = createSelector(
    (state: any) => state.ItemReducer || [],
    (item) => ({
      items: item.itemList,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const { items, rowCount, loading, error } = useSelector(selectItem);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const item_permissions = master_permissions?.modules_permissions?.find(
    (item: any) => item?.modules_name === app_modules?.master?.modules?.item
  );

  //*// UserType with refreashed user(in permission fetch)
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.user_type === "Admin";

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_item") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("itemPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("itemPage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [colValue, setColValue] = useState<any>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  const [syncingItemId, setSyncingItemId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exportAbortControllerRef = useRef<AbortController | null>(null);
  
  // Password Modal State for Sync
  const [showSyncPasswordModal, setShowSyncPasswordModal] = useState(false);
  const [syncPassword, setSyncPassword] = useState("");
  const [showSyncPassword, setShowSyncPassword] = useState(false);
  const [syncType, setSyncType] = useState<"all" | "item" | "selected">("all");
  const [syncItemData, setSyncItemData] = useState<{ sku: string; id: string } | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [webSyncJob, setWebSyncJob] = useState<WebSyncJobSummary | null>(null);
  const webSyncPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const webSyncClearSelectionRef = useRef(false);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Fetch Items
  useEffect(() => {
    dispatch(
      OnfetchItems({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    // colValue,
    sortField,
    sortDirection,
  ]);

  const prevFilters = useRef(colValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(colValue)) {
      prevFilters.current = colValue;
      dispatch(
        OnfetchItems({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    }
  }, [colValue]);

  useEffect(() => {
    setData(items);
  }, [items]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_item") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  // Clear selected items when page changes or data is refetched
  useEffect(() => {
    setSelectedItems([]);
  }, [pageIndex, search, colValue, sorting]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_item: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("itemPage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  useEffect(() => {
    setPageIndex(0);
  }, [columnFilters]);

  const handleStatusToggle = useCallback(
    async (item: any) => {
      const updatedStatus = !item.status;
      const ItemID = item.item_id;

      try {
        await dispatch(onUpdateStatusInItem(ItemID)).unwrap();
        toast.success(
          `Item ${updatedStatus ? "activated" : "deactivated"} successfully`
        );
        dispatch(
          OnfetchItems({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (err) {
        toast.error("Failed to update status");
        console.error(err);
      }
    },
    [dispatch, pageIndex, pageSize, sortField, sortDirection, search, colValue]
  );

  //*// updated Export
  const handleExport = async () => {
    // Create new AbortController for this export
    const abortController = new AbortController();
    exportAbortControllerRef.current = abortController;
    setExporting(true);

    try {
      const params = new URLSearchParams();
      params.append("export", "excel");

      if (search) params.append("search", search);

      if (colValue && colValue.length > 0) {
        colValue.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              !filterField ||
              filterValue === undefined ||
              filterValue === null
            )
              return;

            // If filterValue is an object (like from_date/to_date)
            if (typeof filterValue === "object" && filterValue !== null) {
              if (filterValue.from_date)
                params.append(
                  `${filterField}_date_from`,
                  filterValue.from_date
                );
              if (filterValue.to_date)
                params.append(`${filterField}_date_to`, filterValue.to_date);
            } else {
              // Primitive value
              params.append(filterField, String(filterValue));
            }
          }
        );
      }

      // Step 1: First call – get the file_url (with abort signal)
      const response = await Axios.get(
        `/api/item/export-to-excel/?${params.toString()}`,
        {
          signal: abortController.signal,
        }
      );

      const fileUrl = response.data?.file_url;
      if (!fileUrl) {
        throw new Error("No file_url found in API response");
      }

      // Step 2: Second call – download the file as blob (with abort signal)
      const downloadResponse = await Axios.get(fileUrl, {
        responseType: "blob",
        signal: abortController.signal,
      });

      // Step 3: Get filename from file_url
      const filename = fileUrl.split("/").pop() || "item.xlsx";

      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export completed successfully");
    } catch (error: any) {
      // Don't show error if it was cancelled
      if (error.name === "CanceledError" || error.message === "canceled") {
        toast.info("Export cancelled");
      } else {
        console.error("Export failed", error);
        toast.error("Failed to export CSV");
      }
    } finally {
      setExporting(false);
      exportAbortControllerRef.current = null;
    }
  };

  const handleCancelExport = useCallback(() => {
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
      exportAbortControllerRef.current = null;
      setExporting(false);
    }
  }, []);

  // Price calculation function
  const calculatePrices = useCallback(
    (purchasePrice: number, webPer: number, systemPer: number) => {
      // Sale Price = purchase_price × (web_per + 100) / 100
      const salePrice = Math.round(purchasePrice * ((webPer + 100) / 100));

      // Selling Price = purchase_price × (system_per + 100) / 100
      const sellingPrice = Math.round(
        purchasePrice * ((systemPer + 100) / 100)
      );

      return {
        sale_price: salePrice,
        selling_price: sellingPrice,
      };
    },
    []
  );

  // Handle purchase price update
  const handlePurchasePriceUpdate = useCallback(
    async (itemId: string, newPurchasePrice: number, item: any) => {
      // Prevent multiple simultaneous updates
      if (updatingItemId === itemId) {
        throw new Error("Update already in progress for this item");
      }

      setUpdatingItemId(itemId);

      try {
        // Calculate new prices based on existing web_per and system_per
        const webPer = Number(item.web_per) || 0;
        const systemPer = Number(item.system_per) || 0;
        const calculatedPrices = calculatePrices(
          newPurchasePrice,
          webPer,
          systemPer
        );

        // Create FormData for the update with all required fields
        const formData = new FormData();

        // Required fields from the item data
        formData.append("item_name", item.item_name || "");
        formData.append("item_sku", item.item_sku || "");
        formData.append("gst", item.gst?.toString() || "0");
        formData.append("category", item.category?.toString() || "");
        formData.append("web_per", item.web_per?.toString() || "0");
        formData.append("system_per", item.system_per?.toString() || "0");

        // Price fields (updated and calculated)
        formData.append("purchase_price", newPurchasePrice.toString());
        formData.append("sale_price", calculatedPrices.sale_price.toString());
        formData.append(
          "selling_price",
          calculatedPrices.selling_price.toString()
        );

        // Other existing fields
        formData.append("quantity", item.quantity?.toString() || "0");
        formData.append("regular_price", item.regular_price?.toString() || "0");

        // Optional fields
        if (item.hsn_code) formData.append("hsn_code", item.hsn_code);
        if (item.remarks) formData.append("remarks", item.remarks);
        if (item.tags) formData.append("tags", JSON.stringify(item.tags));
        if (item.attributes)
          formData.append("attributes", JSON.stringify(item.attributes));

        // Update the item
        console.log("Updating item with ID:", itemId);
        console.log("FormData contents:");
        // Debug FormData contents
        formData.forEach((value, key) => {
          console.log(key, value);
        });

        // Direct API call instead of dispatch
        const token = JSON.parse(
          localStorage.getItem("authUser") || "{}"
        )?.access;

        const result = await Axios.put(`api/item/edit/${itemId}/`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("Update result:", result.data);

        toast.success("Purchase price updated successfully");

        // Refresh the data
        dispatch(
          OnfetchItems({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (error: any) {
        console.error("Failed to update purchase price:", error);

        // Handle different types of API errors
        let errorMessage = "Failed to update purchase price";

        if (error?.response?.data) {
          const apiError = error.response.data;

          // Handle validation errors
          if (apiError.errors) {
            if (typeof apiError.errors === "string") {
              errorMessage = apiError.errors;
            } else {
              const errorMessages = Object.values(apiError.errors).flat();
              errorMessage = Array.isArray(errorMessages)
                ? errorMessages.join(", ")
                : String(errorMessages);
            }
          } else if (apiError.message) {
            errorMessage = apiError.message;
          } else if (apiError.detail) {
            errorMessage = apiError.detail;
          }
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.code === "NETWORK_ERROR" || !navigator.onLine) {
          errorMessage = "Network error: Please check your internet connection";
        } else if (
          error?.code === "ECONNABORTED" ||
          error?.message?.includes("timeout")
        ) {
          errorMessage = "Request timeout: Please try again";
        }

        // Show error as toast only (doesn't affect table)
        toast.error(errorMessage);
        throw error; // Re-throw to let EditableCell handle the error state
      } finally {
        setUpdatingItemId(null);
      }
    },
    [
      dispatch,
      calculatePrices,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
      updatingItemId,
    ]
  );

  const handleQuantityUpdate = useCallback(
    async (itemId: string, newQuantity: number, item: any) => {
      // Prevent multiple simultaneous updates
      if (updatingItemId === itemId) {
        throw new Error("Update already in progress for this item");
      }

      setUpdatingItemId(itemId);

      try {
        // Create FormData for the update with all required fields
        const formData = new FormData();

        // Required fields from the item data
        formData.append("item_name", item.item_name || "");
        formData.append("item_sku", item.item_sku || "");
        formData.append("gst", item.gst?.toString() || "0");
        formData.append("category", item.category?.toString() || "");
        formData.append("web_per", item.web_per?.toString() || "0");
        formData.append("system_per", item.system_per?.toString() || "0");

        // Price fields (keep existing values, no calculation needed)
        formData.append("purchase_price", item.purchase_price?.toString() || "0");
        formData.append("sale_price", item.sale_price?.toString() || "0");
        formData.append("selling_price", item.selling_price?.toString() || "0");

        // Other existing fields
        formData.append("quantity", newQuantity.toString());
        formData.append("regular_price", item.regular_price?.toString() || "0");

        // Optional fields
        if (item.hsn_code) formData.append("hsn_code", item.hsn_code);
        if (item.remarks) formData.append("remarks", item.remarks);
        if (item.tags) formData.append("tags", JSON.stringify(item.tags));
        if (item.attributes)
          formData.append("attributes", JSON.stringify(item.attributes));

        // Update the item
        const token = JSON.parse(
          localStorage.getItem("authUser") || "{}"
        )?.access;

        await Axios.put(`api/item/edit/${itemId}/`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Quantity updated successfully");

        // Refresh the data
        dispatch(
          OnfetchItems({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (error: any) {
        console.error("Failed to update quantity:", error);

        // Handle different types of API errors
        let errorMessage = "Failed to update quantity";

        if (error?.response?.data) {
          const apiError = error.response.data;

          // Handle validation errors
          if (apiError.errors) {
            if (typeof apiError.errors === "string") {
              errorMessage = apiError.errors;
            } else {
              const errorMessages = Object.values(apiError.errors).flat();
              errorMessage = Array.isArray(errorMessages)
                ? errorMessages.join(", ")
                : String(errorMessages);
            }
          } else if (apiError.message) {
            errorMessage = apiError.message;
          } else if (apiError.detail) {
            errorMessage = apiError.detail;
          }
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.code === "NETWORK_ERROR" || !navigator.onLine) {
          errorMessage = "Network error: Please check your internet connection";
        } else if (
          error?.code === "ECONNABORTED" ||
          error?.message?.includes("timeout")
        ) {
          errorMessage = "Request timeout: Please try again";
        }

        // Show error as toast only (doesn't affect table)
        toast.error(errorMessage);
        throw error; // Re-throw to let EditableCell handle the error state
      } finally {
        setUpdatingItemId(null);
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
      updatingItemId,
    ]
  );

  const handleDelete = useCallback(
    async (itemId: any) => {
      // const confirmed = window.confirm(
      //   "Are you sure you want to delete this item?"
      // );
      // if (!confirmed) return;
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Are You Sure You Want To Delete this Item!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        width: "350px",
        customClass: {
          popup: "swal2-sm-popup",
        },
      });

      if (!result.isConfirmed) return;

      await dispatch(deleteItemById(itemId))
        .unwrap()
        .then(() => {
          toast.success(`Item deleted successfully!!`);

          if (data.length === 1 && pageIndex > 0) {
            handlePageChange(pageIndex - 1);
          } else {
            // ✅ Re-fetch list after deletion
            dispatch(
              OnfetchItems({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search,
                filters: colValue,
              })
            );
          }
        });
    },
    [
      dispatch,
      data,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
    ]
  );

  const refreshItemGrid = useCallback(() => {
    dispatch(
      OnfetchItems({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  const finishWebSyncJob = useCallback(
    (job: WebSyncJobSummary, clearSelection = false) => {
      persistWebSyncJobId(null);
      setWebSyncJob(null);
      setSyncingAll(false);
      setSyncingItemId(null);
      refreshItemGrid();
      if (clearSelection) {
        setSelectedItems([]);
      }
      if (job.status === "completed") {
        toast.success("Web sync completed successfully.");
      } else if (job.status === "completed_with_errors") {
        toast.warning(
          `Web sync finished with errors (${job.failed_count} failed, ${job.success_count} succeeded).`
        );
      } else if (job.status === "failed") {
        toast.error(job.last_error || "Web sync job failed.");
      }
    },
    [refreshItemGrid]
  );

  const attachWebSyncJob = useCallback((job: WebSyncJobSummary) => {
    persistWebSyncJobId(job.job_id);
    setWebSyncJob(job);
    setSyncingAll(true);
  }, []);

  const beginWebSyncJob = useCallback(
    async (
      body: { password: string; item_sku?: string; item_skus?: string[] },
      options?: { itemId?: string; clearSelectionOnComplete?: boolean }
    ) => {
      setSyncingAll(true);
      if (options?.itemId) {
        setSyncingItemId(options.itemId);
      }
      webSyncClearSelectionRef.current =
        options?.clearSelectionOnComplete === true;
      try {
        const result = await startWebSyncJob(body);
        const job = result.data?.job as WebSyncJobSummary | undefined;
        if (result.status === 409 && result.data?.job_id && job) {
          attachWebSyncJob(job);
          toast.info("Resuming your in-progress web sync job.");
          return;
        }
        if (job?.job_id) {
          attachWebSyncJob(job);
          toast.info("Web sync started — progress updates below.");
        }
      } catch (error: any) {
        console.error("Failed to start web sync job:", error);
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          (error?.response?.status === 503
            ? "Web sync database tables missing — run backend migrate item."
            : "Failed to start web sync. On production, ensure qcluster is running.");
        toast.error(msg);
        setSyncingAll(false);
        setSyncingItemId(null);
      }
    },
    [attachWebSyncJob]
  );

  const handleSync = useCallback(
    async (password: string) => {
      await beginWebSyncJob({ password });
    },
    [beginWebSyncJob]
  );

  const handleSyncItem = useCallback(
    async (itemSku: string, itemId: string, password: string) => {
      await beginWebSyncJob(
        { password, item_sku: itemSku },
        { itemId }
      );
    },
    [beginWebSyncJob]
  );

  const handleSyncClick = useCallback(() => {
    setSyncType("all");
    setSyncItemData(null);
    setSyncPassword("");
    setShowSyncPassword(false);
    setShowSyncPasswordModal(true);
    // Clear input field after modal opens
    setTimeout(() => {
      const input = document.getElementById("web-sync-password-input") as HTMLInputElement;
      if (input) {
        input.value = "";
        setSyncPassword("");
      }
    }, 100);
  }, []);

  const handleSyncItemClick = useCallback((itemSku: string, itemId: string) => {
    setSyncType("item");
    setSyncItemData({ sku: itemSku, id: itemId });
    setSyncPassword("");
    setShowSyncPassword(false);
    setShowSyncPasswordModal(true);
    // Clear input field after modal opens
    setTimeout(() => {
      const input = document.getElementById("web-sync-password-input") as HTMLInputElement;
      if (input) {
        input.value = "";
        setSyncPassword("");
      }
    }, 100);
  }, []);

  const handleCloseSyncPasswordModal = useCallback(() => {
    setSyncPassword("");
    setShowSyncPassword(false);
    setSyncItemData(null);
    setShowSyncPasswordModal(false);
    // Clear the input field explicitly
    setTimeout(() => {
      const input = document.getElementById("web-sync-password-input") as HTMLInputElement;
      if (input) {
        input.value = "";
      }
    }, 100);
  }, []);

  // Bulk sync function for selected items
  const handleSyncSelectedItems = useCallback(
    async (selectedSkus: string[], password: string) => {
      await beginWebSyncJob(
        { password, item_skus: selectedSkus },
        { clearSelectionOnComplete: true }
      );
    },
    [beginWebSyncJob]
  );

  useEffect(() => {
    const restore = async () => {
      try {
        const active = await fetchActiveWebSyncJob();
        if (active && !isWebSyncJobTerminal(active.status)) {
          attachWebSyncJob(active);
          return;
        }
        const stored = readStoredWebSyncJobId();
        if (stored) {
          const job = await fetchWebSyncJob(stored);
          if (job && !isWebSyncJobTerminal(job.status)) {
            attachWebSyncJob(job);
          } else {
            persistWebSyncJobId(null);
          }
        }
      } catch (err) {
        console.error("Failed to restore web sync job:", err);
      }
    };
    restore();
  }, [attachWebSyncJob]);

  useEffect(() => {
    if (!webSyncJob || isWebSyncJobTerminal(webSyncJob.status)) {
      return;
    }

    let delayMs = 2000;
    let errorStreak = 0;

    const schedulePoll = () => {
      webSyncPollRef.current = setTimeout(async () => {
        try {
          const job = await fetchWebSyncJob(webSyncJob.job_id);
          if (!job) {
            schedulePoll();
            return;
          }
          setWebSyncJob(job);
          errorStreak = 0;
          delayMs = 2000;
          if (isWebSyncJobTerminal(job.status)) {
            finishWebSyncJob(job, webSyncClearSelectionRef.current);
            return;
          }
        } catch (err: any) {
          const isGone =
            err?.response?.status === 404 &&
            webSyncJob &&
            webSyncJob.processed_count >= webSyncJob.total_count &&
            webSyncJob.total_count > 0;
          if (isGone) {
            finishWebSyncJob(webSyncJob, webSyncClearSelectionRef.current);
            return;
          }
          console.error("Web sync status poll failed:", err);
          errorStreak += 1;
          delayMs = Math.min(30000, 2000 * Math.pow(2, errorStreak));
        }
        schedulePoll();
      }, delayMs);
    };

    schedulePoll();
    return () => {
      if (webSyncPollRef.current) {
        clearTimeout(webSyncPollRef.current);
      }
    };
  }, [webSyncJob?.job_id, webSyncJob?.status, finishWebSyncJob]);

  const handleConfirmSync = useCallback(async () => {
    if (!syncPassword.trim()) {
      toast.error("Please enter a password");
      return;
    }

    // Prevent password managers from triggering
    const passwordInput = document.getElementById("web-sync-password-input") as HTMLInputElement;
    if (passwordInput) {
      passwordInput.blur();
      // Temporarily change type to text to prevent password manager detection
      const originalType = passwordInput.type;
      passwordInput.type = "text";
      setTimeout(() => {
        passwordInput.type = originalType;
      }, 100);
    }

    handleCloseSyncPasswordModal();

    if (syncType === "all") {
      await handleSync(syncPassword);
    } else if (syncType === "item" && syncItemData) {
      await handleSyncItem(syncItemData.sku, syncItemData.id, syncPassword);
    } else if (syncType === "selected") {
      await handleSyncSelectedItems(selectedItems, syncPassword);
    }
  }, [syncPassword, syncType, syncItemData, selectedItems, handleSync, handleSyncItem, handleSyncSelectedItems, handleCloseSyncPasswordModal]);

  // Handle individual item selection
  const handleItemSelect = useCallback((itemSku: string, isSelected: boolean) => {
    setSelectedItems(prev =>
      isSelected
        ? [...prev, itemSku]
        : prev.filter(sku => sku !== itemSku)
    );
  }, []);

  // Handle select all items on current page
  const handleSelectAll = useCallback((isSelected: boolean) => {
    if (isSelected) {
      const allSkus = data.map(item => item.item_sku).filter(Boolean);
      setSelectedItems(allSkus);
    } else {
      setSelectedItems([]);
    }
  }, [data]);

  // Handle sync selected items click
  const handleSyncSelectedClick = useCallback(() => {
    if (selectedItems.length === 0) {
      toast.warning("Please select items to sync");
      return;
    }
    setSyncType("selected");
    setSyncItemData(null);
    setSyncPassword("");
    setShowSyncPassword(false);
    setShowSyncPasswordModal(true);
    // Clear input field after modal opens
    setTimeout(() => {
      const input = document.getElementById("web-sync-password-input") as HTMLInputElement;
      if (input) {
        input.value = "";
        setSyncPassword("");
      }
    }, 100);
  }, [selectedItems.length]);

  const downloadImportFailedReport = useCallback(
    async (data: {
      failed_report_url?: string | null;
      failed_report_path?: string | null;
      failed_report_filename?: string | null;
    }) => {
      const path =
        data.failed_report_path ||
        (data.failed_report_url?.includes("/media/")
          ? data.failed_report_url.substring(
              data.failed_report_url.indexOf("/media/")
            )
          : null);
      if (!path) {
        return false;
      }
      const downloadResponse = await Axios.get(path, { responseType: "blob" });
      const filename =
        data.failed_report_filename ||
        path.split("/").pop() ||
        "item_import_failed.xlsx";
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    },
    []
  );

  const handleBulkUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const result = await Axios.post("api/item/excel-import/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const failedCount =
          result.data?.failed_count ?? result.data?.skipped_rows?.length ?? 0;

        // Check if there are failed rows (validation / processing errors)
        if (failedCount > 0) {
          let reportDownloaded = false;
          if (
            result.data?.failed_report_path ||
            result.data?.failed_report_url
          ) {
            try {
              reportDownloaded = await downloadImportFailedReport(result.data);
            } catch (downloadErr) {
              console.error("Failed report download error:", downloadErr);
            }
          }

          // Show modal with failure details
          const skippedRows = result.data.skipped_rows || [];
          const skippedRowsHtml = skippedRows
            .map(
              (row: any) => `
          <div style="margin-bottom: 10px; padding: 8px; background-color: #f8f9fa; border-left: 3px solid #dc3545; border-radius: 4px; font-size: 0.9rem;">
            <strong>Row ${row.row}:</strong> ${row.reason}
          </div>
        `
            )
            .join("");

          await Swal.fire({
            title: "Upload Completed with Errors",
            html: `
            <div style="text-align: left; font-size: 0.9rem;">
              <p><strong>Imported:</strong> ${result.data.imported || 0}</p>
              <p><strong>Created:</strong> ${result.data.created_count ?? 0}</p>
              <p><strong>Updated:</strong> ${result.data.updated_count ?? 0}</p>
              <p><strong>Unchanged (skipped):</strong> ${result.data.skipped_count ?? 0}</p>
              <p><strong>Failed:</strong> ${failedCount}</p>
              ${
                reportDownloaded
                  ? `<p><strong>Failed rows report:</strong> downloaded to your device.</p>`
                  : result.data.failed_report_path || result.data.failed_report_url
                    ? `<p><em>Failed rows report is available; download did not start automatically. Check browser pop-up blockers or try again from network tab.</em></p>`
                    : result.data.failed_report_error
                      ? `<p style="color:#dc3545;">${result.data.failed_report_error}</p>`
                      : `<p><em>No failure report — only validation failures generate a report. Unchanged price rows are not failures.</em></p>`
              }
              <div style="margin-top: 10px; max-height: 400px; overflow-y: auto;">
                <strong style="margin-bottom: 2px;">Skipped Rows:</strong>
                ${skippedRowsHtml}
              </div>
            </div>
          `,
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonColor: "#0073BB",
            width: "600px",
            customClass: {
              popup: "swal2-sm-popup",
              confirmButton: "swal2-confirm-custom",
            },
            didOpen: () => applyItemSwalPopupStyles(Swal.getPopup()),
          });
        } else {
          // Success case
          await Swal.fire({
            title: "Upload successful",
            html: `
            <div style="font-size: 0.9rem; line-height: 1.6;">
              <p style="margin: 0 0 6px;"><strong>Created:</strong> ${result.data?.created_count ?? 0}</p>
              <p style="margin: 0 0 6px;"><strong>Updated:</strong> ${result.data?.updated_count ?? 0}</p>
              <p style="margin: 0 0 6px;"><strong>Unchanged (prices):</strong> ${result.data?.skipped_count ?? 0}</p>
              <p style="margin: 0;"><strong>Imported total:</strong> ${result.data?.imported ?? 0}</p>
            </div>
          `,
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#0073BB",
            width: "480px",
            customClass: {
              popup: "swal2-sm-popup",
              confirmButton: "swal2-confirm-custom",
            },
            didOpen: () => applyItemSwalPopupStyles(Swal.getPopup()),
          });
        }

        // Refresh the data after upload
        dispatch(
          OnfetchItems({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (error: any) {
        console.error("Bulk upload failed:", error);

        // Handle error response with skipped rows
        if (error?.response?.data?.skipped_rows) {
          const skippedRows = error.response.data.skipped_rows || [];
          const skippedRowsHtml = skippedRows
            .map(
              (row: any) => `
          <div style="margin-bottom: 10px; padding: 8px; background-color: #f8f9fa; border-left: 3px solid #dc3545; border-radius: 4px;">
            <strong>Row ${row.row}:</strong> ${row.reason}
          </div>
        `
            )
            .join("");

          await Swal.fire({
            title: "Upload Failed",
            html: `
            <div style="text-align: left;">
              <p><strong>Imported:</strong> ${
                error.response.data.imported || 0
              }</p>
              <p><strong>Skipped:</strong> ${
                error.response.data.skipped_count || 0
              }</p>
              <div style="margin-top: 15px; max-height: 400px; overflow-y: auto;">
                <strong>Skipped Rows:</strong>
                ${skippedRowsHtml}
              </div>
            </div>
          `,
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#0073BB",
            width: "600px",
            customClass: {
              popup: "swal2-sm-popup",
              confirmButton: "swal2-confirm-custom",
            },
            didOpen: () => applyItemSwalPopupStyles(Swal.getPopup()),
          });
        } else {
          toast.error("Failed to upload file. Please try again.");
        }
      } finally {
        setUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
      downloadImportFailedReport,
    ]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        // Validate file type
        const validExtensions = [
          ".xlsx",
          ".xls",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
        ];
        const fileExtension = file.name
          .substring(file.name.lastIndexOf("."))
          .toLowerCase();
        const isValidType =
          validExtensions.includes(fileExtension) ||
          validExtensions.includes(file.type);

        if (!isValidType) {
          toast.error("Please upload a valid Excel file (.xlsx or .xls)");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        handleBulkUpload(file);
      }
    },
    [handleBulkUpload]
  );

  const handleDownloadExcelTemplate = useCallback(async () => {
    try {
      // Fetch the Excel file from the public folder
      const response = await fetch("/Item Import.xlsx");
      if (!response.ok) {
        throw new Error("File not found");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Item Import.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Excel template downloaded successfully");
    } catch (error) {
      console.error("Error downloading Excel template:", error);
      toast.error("Failed to download Excel template");
    }
  }, []);

  const handleBulkUploadClick = useCallback(async () => {
    const result = await Swal.fire({
      title: "Bulk Upload Requirements",
      html: `
        <div style="text-align: left; max-width: 500px; font-size: 0.8rem;">
          <div style="margin-bottom: 15px;">
            <ul style="padding-left: 18px; margin-bottom: 0; line-height: 1.5; font-size: 0.9rem;">
              <li style="margin-bottom: 8px;">
                <strong>Category & Brand:</strong> Items can only be created if their Category and Brand already exist in the respective master records. Items with missing Category or Brand will be skipped.
              </li>
              <li style="margin-bottom: 8px;">
                <strong>Build Your PC Categories:</strong> Categories required for "Build Your PC" should not be uploaded via bulk upload.
              </li>
              <li style="margin-bottom: 8px;">
                <strong>Mandatory Fields:</strong> If any mandatory field is missing, the item will be skipped during import.
              </li>
            </ul>
          </div>
          <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 10px; border-radius: 4px; margin-top: 12px;">
            <p style="margin: 0; color: #856404; font-size: 0.75rem;">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Note:</strong> Please ensure all required masters are created before proceeding with the bulk upload.
            </p>
          </div>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Upload Excel File",
      denyButtonText: "Download Format",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0073BB",
      denyButtonColor: "#0073BB",
      cancelButtonColor: "#0073BB",
      width: "600px",
      customClass: {
        popup: "swal2-sm-popup",
        confirmButton: "swal2-confirm-custom",
        denyButton: "swal2-deny-custom",
        cancelButton: "swal2-cancel-custom",
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        applyItemSwalPopupStyles(popup);
        const confirmButton = popup?.querySelector(
          ".swal2-confirm"
        ) as HTMLElement;
        const denyButton = popup?.querySelector(
          ".swal2-deny"
        ) as HTMLElement;
        const cancelButton = popup?.querySelector(
          ".swal2-cancel"
        ) as HTMLElement;
        const actions = popup?.querySelector(".swal2-actions") as HTMLElement;

        // Style confirm button to match btn-primary
        if (confirmButton) {
          confirmButton.style.backgroundColor = "#0073BB";
          confirmButton.style.borderColor = "#0073BB";
          confirmButton.style.color = "#ffffff";
          confirmButton.style.padding = "0.5rem 1rem";
          confirmButton.style.fontSize = "0.9rem";
          confirmButton.style.height = "auto";
          confirmButton.style.minHeight = "32px";
        }
        // Style deny button (Download Format) to match btn-info
        if (denyButton) {
          denyButton.style.backgroundColor = "#ffffff";
          denyButton.style.borderColor = "#0073BB";
          denyButton.style.borderWidth = "1px";
          denyButton.style.borderStyle = "solid";
          denyButton.style.color = "#0073BB";
          denyButton.style.padding = "0.5rem 1rem";
          denyButton.style.fontSize = "0.9rem";
          denyButton.style.height = "auto";
          denyButton.style.minHeight = "32px";

          // Add hover styles to match btn-secondary
          denyButton.addEventListener("mouseenter", () => {
            denyButton.style.color = "#005D97";
            denyButton.style.borderColor = "#005D97";
            denyButton.style.backgroundColor = "#edecec";
          });
          denyButton.addEventListener("mouseleave", () => {
            denyButton.style.color = "#0073BB";
            denyButton.style.borderColor = "#0073BB";
            denyButton.style.backgroundColor = "#ffffff";
          });
        }
        // Style cancel button to match btn-secondary (variant="secondary")
        if (cancelButton) {
          cancelButton.style.backgroundColor = "#ffffff";
          cancelButton.style.borderColor = "#0073BB";
          cancelButton.style.borderWidth = "1px";
          cancelButton.style.borderStyle = "solid";
          cancelButton.style.color = "#0073BB";
          cancelButton.style.padding = "0.5rem 1rem";
          cancelButton.style.fontSize = "0.9rem";
          cancelButton.style.height = "auto";
          cancelButton.style.minHeight = "32px";
          // Add hover styles to match btn-secondary
          cancelButton.addEventListener("mouseenter", () => {
            cancelButton.style.color = "#005D97";
            cancelButton.style.borderColor = "#005D97";
            cancelButton.style.backgroundColor = "#edecec";
          });
          cancelButton.addEventListener("mouseleave", () => {
            cancelButton.style.color = "#0073BB";
            cancelButton.style.borderColor = "#0073BB";
            cancelButton.style.backgroundColor = "#ffffff";
          });
        }
        // Reorder buttons: Download Format should appear before Upload Excel File
        if (actions && denyButton && confirmButton) {
          // Store buttons in desired order: Deny (Download Format), Confirm (Upload Excel File), Cancel
          const orderedButtons = [denyButton, confirmButton];
          if (cancelButton) {
            orderedButtons.push(cancelButton);
          }
          // Clear actions container
          actions.innerHTML = "";
          // Append buttons in desired order
          orderedButtons.forEach(btn => {
            actions.appendChild(btn);
          });
        }
      },
    });

    if (result.isConfirmed) {
      // Trigger file input after user confirms
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 100);
    } else if (result.isDenied) {
      // Download Excel format when deny button is clicked
      handleDownloadExcelTemplate();
    }
  }, [handleDownloadExcelTemplate]);

  const DynamicAction = useMemo(
    () => [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <ItemActionMenu
            row={row}
            handleDelete={handleDelete}
            handleSync={
              !row.original.is_sync
                ? (itemSku: string) =>
                    handleSyncItemClick(itemSku, row.original.item_id)
                : null
            }
            canEdit={item_permissions?.permissions?.Update === true}
            canDelete={isAdmin}
            syncingItemId={syncingItemId}
          />
        ),
      },
    ],
    [
      handleDelete,
      isAdmin,
      item_permissions?.permissions?.Update,
      handleSyncItem,
      syncingItemId,
    ]
  );

  const columns = useMemo(
    () => [
      {
        header: ({ table }: { table: any }) => {
          // if (item_permissions?.permissions?.Update !== true) {
          //   return null;
          // }
          const isAllSelected = data.length > 0 && selectedItems.length === data.length;
          const isIndeterminate = selectedItems.length > 0 && selectedItems.length < data.length;

          return (
            <input
              type="checkbox"
              checked={isAllSelected}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate;
              }}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="form-check-input"
            />
          );
        },
        id: "select",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          // if (item_permissions?.permissions?.Update !== true) {
          //   return null;
          // }
          return (
            <input
              type="checkbox"
              checked={selectedItems.includes(row.original.item_sku)}
              onChange={(e) => handleItemSelect(row.original.item_sku, e.target.checked)}
              className="form-check-input"
            />
          );
        },
      },
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=item_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              minWidth: "200px",
              maxWidth: "250px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            <span>{row.original.item_name || "-"}</span>
            {/* {row.original.item_is_new === true && (
              <span className="badge bg-info text-white ms-2">New</span>
            )} */}
          </div>
        ),
      },
      {
        header: "Item SKU",
        accessorKey: "item_sku",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=item_sku",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.item_sku || "-"}
          </div>
        ),
      },
      {
        header: "Category",
        accessorKey: "category__category_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=category__category_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minWidth: "120px",
              maxWidth: "250px",
            }}
          >
            {row.original.category_name || "-"}
          </div>
        ),
      },
      {
        header: "Brand",
        accessorKey: "brand__brand_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=brand__brand_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minWidth: "120px",
              maxWidth: "250px",
            }}
          >
            {row.original.brand_name || "-"}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const item = row.original;

          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() =>
                item_permissions?.permissions?.Update &&
                handleStatusToggle(item)
              }
            >
              <i
                className={`bi ${
                  item.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Selling Price",
        accessorKey: "selling_price",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=selling_price",
        cell: ({ row }) => (
          <span className="">
            ₹{" "}
            {Number(row.original.selling_price || 0).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
          </span>
        ),
      },
      {
        header: "Purchase Price",
        accessorKey: "purchase_price",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=purchase_price",
        cell: ({ row }: { row: any }) => {
          if (item_permissions?.permissions?.Update === true) {
            return (
              <EditableCell
                value={Number(row.original.purchase_price || 0)}
                onSave={(newValue) =>
                  handlePurchasePriceUpdate(
                    row.original.item_id,
                    newValue,
                    row.original
                  )
                }
                disabled={
                  !item_permissions?.permissions?.Update ||
                  updatingItemId === row.original.item_id
                }
                integerOnly={true}
              />
            );
          } else {
            return (
              <span className="">
                ₹{" "}
                {Number(row.original.purchase_price || 0).toLocaleString(
                  "en-IN",
                  { maximumFractionDigits: 2 }
                )}
              </span>
            );
          }
        },
      },
      {
        header: "In Stock",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=quantity",
        cell: ({ row }: { row: any }) => {
          if (item_permissions?.permissions?.Update === true) {
            return (
              <EditableCell
                value={Number(row.original.quantity || 0)}
                onSave={(newValue) =>
                  handleQuantityUpdate(
                    row.original.item_id,
                    newValue,
                    row.original
                  )
                }
                disabled={
                  !item_permissions?.permissions?.Update ||
                  updatingItemId === row.original.item_id
                }
                integerOnly={true}
                formatValue={(val: number) =>
                  Number(val || 0).toLocaleString("en-IN")
                }
              />
            );
          } else {
            return (
              <span className="">
                {Number(row.original.quantity || 0).toLocaleString("en-IN")}
              </span>
            );
          }
        },
      },
      {
        header: "HSN Code",
        accessorKey: "hsn_code__hsn_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=hsn_code__hsn_code",
        cell: ({ row }: { row: any }) => {
          return row.original.hsn_name || "-";
        },
      },
      {
        header: "GST%",
        accessorKey: "gst",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/api/item/column-filter-dropdown/?column_filter=gst",
      },

      // {
      //   header: "New Item",
      //   accessorKey: "item_is_new",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl:
      //     "/api/item/column-filter-dropdown/?column_filter=item_is_new",
      //   cell: ({ row }: { row: any }) =>
      //     row.original.item_is_new === true ? "Yes" : "",
      // },

      {
        header: "Sync Status",
        accessorKey: "is_sync",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          const isCurrentlySyncing = syncingItemId === row.original.item_id;

          if (isCurrentlySyncing) {
            return (
              <div className="d-flex flex-column align-items-start gap-1">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Syncing...</span>
                  </div>
                  <span className="text-primary">Syncing...</span>
                </div>
                {/* <PriceStatusChip status={row.original.price_status} /> */}
              </div>
            );
          }

          return (
            <div className="d-flex flex-column align-items-start gap-1">
              <span
                className={`badge text-white ${
                  row.original.is_sync ? "bg-success" : "bg-danger"
                } text-dark`}
              >
                {row.original.is_sync ? "Synced" : "Not Synced"}
              </span>
              {/* <PriceStatusChip status={row.original.price_status} /> */}
            </div>
          );
        },
      },
      
      {
        header: "Price Status",
        accessorKey: "price_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/item/column-filter-dropdown/?column_filter=price_status",
        cell: ({ row }: { row: any }) => (
          <PriceStatusChip status={row.original.price_status} />
        ),
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,
    ],
    [
      handleStatusToggle,
      DynamicAction,
      item_permissions?.permissions?.Update,
      handlePurchasePriceUpdate,
      handleQuantityUpdate,
      updatingItemId,
      syncingItemId,
      selectedItems,
      handleSelectAll,
      handleItemSelect,
      data,
    ]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={2} xl={1} className="me-auto">
                      <h6 className="card-title mb-0">Item</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    {selectedItems.length <= 0 && (
                      <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                        <SearchBox
                          value={searchInput}
                          onChange={setSearchInput}
                        />
                      </Col>
                    )}

                    <Col
                      xs={12}
                      sm={12}
                      md="auto"
                      lg="auto"
                      xl="auto"
                      className="mt-2 mt-md-1"
                    >
                      <div className="d-flex flex-wrap gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileInputChange}
                          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                          style={{ display: "none" }}
                        />
                        {item_permissions?.permissions?.Create === true && (
                          <div
                            className={`btn btn-primary ${
                              uploading ? "disabled" : ""
                            }`}
                            onClick={
                              uploading ? undefined : handleBulkUploadClick
                            }
                            style={{
                              cursor: uploading ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {uploading ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <span className="d-none d-sm-inline">
                                  Uploading...
                                </span>
                                <span className="d-inline d-sm-none">
                                  Upload...
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-upload align-baseline me-1"></i>{" "}
                                <span className="d-none d-sm-inline">
                                  Bulk Upload
                                </span>
                                <span className="d-inline d-sm-none">
                                  Upload
                                </span>
                              </>
                            )}
                          </div>
                        )}

                        {item_permissions?.permissions?.View === true && (
                          <div
                            className={`hstack gap-2 btn btn-light ${
                              exporting ? "" : ""
                            }`}
                            onClick={
                              exporting ? handleCancelExport : handleExport
                            }
                            style={{
                              cursor: "pointer",
                              position: "relative",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {exporting ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <span className="d-none d-sm-inline">
                                  Exporting...
                                </span>
                                <span className="d-inline d-sm-none">
                                  Export...
                                </span>
                                <i
                                  className="bi bi-x-lg ms-2"
                                  style={{
                                    fontSize: "0.875rem",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelExport();
                                  }}
                                ></i>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                                Export
                              </>
                            )}
                          </div>
                        )}

                        {selectedItems.length <= 0 && (
                          <div
                            className={`btn btn-primary ${
                              syncingAll ? "disabled" : ""
                            }`}
                            onClick={syncingAll ? undefined : handleSyncClick}
                            style={{
                              cursor: syncingAll ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {syncingAll ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <span className="d-none d-sm-inline">
                                  Syncing All...
                                </span>
                                <span className="d-inline d-sm-none">
                                  Syncing...
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-repeat align-baseline me-1"></i>{" "}
                                <span className="d-none d-sm-inline">
                                  Web Sync
                                </span>
                                <span className="d-inline d-sm-none">Sync</span>
                              </>
                            )}
                          </div>
                        )}

                        {selectedItems.length > 0 && (
                          <div
                            className={`btn btn-success ${syncingAll ? "disabled" : ""}`}
                            onClick={syncingAll ? undefined : handleSyncSelectedClick}
                            style={{
                              cursor: syncingAll ? "not-allowed" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {syncingAll ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                >
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                <span className="d-none d-sm-inline">
                                  Syncing Selected...
                                </span>
                                <span className="d-inline d-sm-none">
                                  Syncing...
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-square align-baseline me-1"></i>{" "}
                                <span className="d-none d-sm-inline">
                                  Sync Selected ({selectedItems.length})
                                </span>
                                <span className="d-inline d-sm-none">
                                  Sync ({selectedItems.length})
                                </span>
                              </>
                            )}
                          </div>
                        )}

                        {item_permissions?.permissions?.View === true && (
                          <Link
                            to="/web-sync-log"
                            className="btn btn-secondary"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="bi bi-arrow-repeat align-baseline me-1"></i>{" "}
                            <span className="d-none d-sm-inline">
                              Web Sync Log
                            </span>
                            <span className="d-inline d-sm-none">Log</span>
                          </Link>
                        )}

                        {item_permissions?.permissions?.Create === true && (
                          <Link
                            to="/add-item"
                            className="btn btn-primary"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            <span className="d-none d-sm-inline">Add Item</span>
                            <span className="d-inline d-sm-none">Add</span>
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                {webSyncJob && (
                  <Card.Body className="pt-3 pb-0">
                    <Alert
                      variant={
                        webSyncJob.status === "completed"
                          ? "success"
                          : webSyncJob.status === "completed_with_errors"
                            ? "warning"
                            : webSyncJob.status === "failed"
                              ? "danger"
                              : "info"
                      }
                      className="mb-0"
                    >
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                        <strong>
                          Web Sync Progress: {webSyncJob.processed_count} /{" "}
                          {webSyncJob.total_count}
                        </strong>
                        <span className="text-muted small text-capitalize">
                          {webSyncJob.status.replace(/_/g, " ")}
                        </span>
                      </div>
                      <ProgressBar
                        now={webSyncJob.progress_percent}
                        label={`${webSyncJob.progress_percent}%`}
                        animated={!isWebSyncJobTerminal(webSyncJob.status)}
                        striped={!isWebSyncJobTerminal(webSyncJob.status)}
                      />
                      <div className="small mt-2 d-flex flex-wrap gap-3">
                        <span>
                          <strong>Successful:</strong> {webSyncJob.success_count}
                        </span>
                        <span>
                          <strong>Failed:</strong> {webSyncJob.failed_count}
                        </span>
                        <span>
                          <strong>Pending:</strong> {webSyncJob.pending_count}
                        </span>
                      </div>
                      {webSyncJob.last_error && (
                        <div className="small mt-2 text-danger">
                          {webSyncJob.last_error}
                        </div>
                      )}
                    </Alert>
                  </Card.Body>
                )}
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search Items..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Sync Password Modal */}
      <Modal
        show={showSyncPasswordModal}
        onHide={handleCloseSyncPasswordModal}
        autoFocus={false}
        onEntered={() => {
          // Clear password when modal is fully shown
          setSyncPassword("");
          const input = document.getElementById("web-sync-password-input") as HTMLInputElement;
          if (input) {
            input.value = "";
            input.focus();
          }
          // Prevent autofill on search box - check and clear if email was autofilled
          setTimeout(() => {
            const searchInputElement = document.getElementById("search-input") as HTMLInputElement;
            if (searchInputElement) {
              const currentValue = searchInputElement.value;
              // Check if the value looks like an email (autofilled)
              const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (emailPattern.test(currentValue)) {
                // Clear the search input
                setSearchInput("");
                searchInputElement.value = "";
              }
            }
          }, 100);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {syncType === "all" ? "Web Sync All Items" : "Sync Item"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (syncPassword.trim()) {
                handleConfirmSync();
              }
              return false;
            }}
            style={{ margin: 0 }}
          >
            <Form.Group>
              <Form.Label>Web Sync Password</Form.Label>
              <div style={{ position: "relative" }}>
                <Form.Control
                  type={showSyncPassword ? "text" : "password"}
                  placeholder="Enter web sync password"
                  value={syncPassword}
                  onChange={(e) => setSyncPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && syncPassword.trim()) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleConfirmSync();
                    }
                  }}
                  style={{ paddingRight: "40px" }}
                  autoComplete="off"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-dashlane-ignore="true"
                  name="web-sync-password-input"
                  id="web-sync-password-input"
                />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSyncPassword(!showSyncPassword);
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0",
                  color: "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                tabIndex={-1}
              >
                <i
                  className={showSyncPassword ? "bi bi-eye-slash" : "bi bi-eye"}
                  style={{ fontSize: "1.1rem" }}
                ></i>
              </button>
            </div>
          </Form.Group>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCloseSyncPasswordModal}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!syncPassword.trim()}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              // Prevent password manager from detecting form submission
              const form = e.currentTarget.closest("form");
              if (form) {
                form.setAttribute("autocomplete", "off");
              }
              handleConfirmSync();
            }}
          >
            {syncType === "all" ? "Sync All" : "Sync Item"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default Item;
